import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Upload, Phone, Mail, MapPin, Clock, X } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import axios from "axios";
import { useSite, useCompany } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { PageHero, Loading } from "@/components/site/Blocks";

function GoogleMapCard() {
  const { site } = useSite();
  const c = useCompany();
  const g = site?.settings?.google_reviews || {};
  const q = encodeURIComponent(`${c.name}, ${c.city}, ${c.state}`);
  return (
    <section className="bg-[color:var(--ib-off)] px-5 py-16 sm:px-8 lg:px-16" data-testid="contact-map-section">
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_2fr]">
        <div>
          <p className="ib-eyebrow">Find us on Google</p>
          <h2 className="mt-3 font-display text-3xl uppercase">Serving Southwest Florida</h2>
          <p className="mt-4 text-black/70">{c.service_area_summary}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {g.url && <a href={g.url} target="_blank" rel="noreferrer" className="ib-btn ib-btn-primary" data-testid="contact-view-google-btn">View us on Google</a>}
            {g.write_url && <a href={g.write_url} target="_blank" rel="noreferrer" className="ib-btn ib-btn-outline" data-testid="contact-review-google-btn">Review us on Google</a>}
          </div>
        </div>
        <iframe title="Ironbark Construction service area map" src={`https://maps.google.com/maps?q=${q}&z=9&output=embed`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-80 w-full border-0 lg:h-96" data-testid="contact-google-map" />
      </div>
    </section>
  );
}

const inputCls = "w-full border border-black/15 bg-white px-3.5 py-3 text-base outline-none transition-colors focus:border-[color:var(--ib-orange)]";

function Field({ label, children, req }) {
  return <label className="block"><span className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-black/70">{label}{req && <span className="text-[color:var(--ib-orange)]"> *</span>}</span>{children}</label>;
}

function Select({ name, value, onChange, options, testid }) {
  return (
    <select name={name} value={value} onChange={onChange} className={inputCls} data-testid={testid}>
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function Contact() {
  const { site } = useSite();
  const c = useCompany();
  const [params] = useSearchParams();
  const started = useRef(Date.now());
  const fileRef = useRef(null);
  const svc = params.get("service");
  const similar = params.get("similar");
  const [f, setF] = useState({ name: "", email: "", phone: "", location: "", category: "", description: similar ? `I'm interested in a project similar to "${similar}".` : "", budget: "", timeline: "", contact_method: "", owns_property: "", website: "", privacy_ack: false });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  useSeo("contact");
  if (!site) return <Loading />;
  const form = site.settings.form || {};
  if (svc && !f.category) {
    const match = (form.categories || []).find((x) => x.toLowerCase().includes(svc.toLowerCase().split(" ")[0]));
    if (match) setF((s) => ({ ...s, category: match }));
  }
  const on = (e) => setF({ ...f, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const addFiles = (list) => {
    const max = (form.max_file_mb || 15) * 1024 * 1024;
    const ok = [...list].filter((x) => {
      const ext = x.name.split(".").pop().toLowerCase();
      if (!(form.allowed_ext || []).includes(ext)) { setErr(`.${ext} files are not accepted.`); return false; }
      if (x.size > max) { setErr(`${x.name} is larger than ${form.max_file_mb} MB.`); return false; }
      return true;
    });
    setFiles((s) => [...s, ...ok].slice(0, 10));
  };
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.privacy_ack) return setErr("Please acknowledge the privacy notice.");
    setBusy(true);
    const fd = new FormData();
    fd.append("data", JSON.stringify({ ...f, started_at: started.current, page: "/contact" }));
    files.forEach((x) => fd.append("files", x));
    try {
      const { data } = await axios.post(`${API}/public/quote`, fd);
      setDone(data.message);
    } catch (ex) {
      setErr(errMsg(ex));
    }
    setBusy(false);
  };
  return (
    <main>
      <PageHero eyebrow="Contact / Get a Quote" title="Let's talk about your project" text="Free estimates are available upon request. Tell us a little about what you're planning and we'll be in touch." image="/media/hero-entry.jpg" />
      <section className="bg-[color:var(--ib-off)] py-16">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 lg:grid-cols-[1.6fr_1fr] lg:px-8">
          {done ? (
            <div className="flex flex-col items-start gap-4 bg-white p-10" data-testid="quote-success">
              <CheckCircle2 size={44} className="text-[color:var(--ib-orange)]" />
              <h2 className="font-display text-3xl uppercase">Request received</h2>
              <p className="max-w-lg text-black/70">{done}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5 bg-white p-6 sm:grid-cols-2 sm:p-10" data-testid="quote-form" noValidate>
              <Field label="Full name" req><input name="name" value={f.name} onChange={on} required className={inputCls} autoComplete="name" data-testid="quote-name" /></Field>
              <Field label="Email" req><input name="email" type="email" value={f.email} onChange={on} className={inputCls} autoComplete="email" data-testid="quote-email" /></Field>
              <Field label="Phone"><input name="phone" type="tel" value={f.phone} onChange={on} className={inputCls} autoComplete="tel" data-testid="quote-phone" /></Field>
              <Field label="Project address or city"><input name="location" value={f.location} onChange={on} className={inputCls} data-testid="quote-location" /></Field>
              <Field label="Project category"><Select name="category" value={f.category} onChange={on} options={form.categories || []} testid="quote-category" /></Field>
              <Field label="Do you own the property?"><Select name="owns_property" value={f.owns_property} onChange={on} options={["Yes", "No", "In the process of buying"]} testid="quote-owns" /></Field>
              <div className="sm:col-span-2"><Field label="Project description" req><textarea name="description" value={f.description} onChange={on} rows={5} className={inputCls} data-testid="quote-description" /></Field></div>
              <Field label="Estimated budget (optional)"><Select name="budget" value={f.budget} onChange={on} options={form.budgets || []} testid="quote-budget" /></Field>
              <Field label="Desired start timeline"><Select name="timeline" value={f.timeline} onChange={on} options={form.timelines || []} testid="quote-timeline" /></Field>
              <Field label="Preferred contact method"><Select name="contact_method" value={f.contact_method} onChange={on} options={form.contact_methods || []} testid="quote-contact-method" /></Field>
              <input name="website" value={f.website} onChange={on} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <div className="sm:col-span-2">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-black/70">Plans or inspiration photos (optional)</span>
                <button type="button" onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                  className="flex w-full flex-col items-center gap-2 border-2 border-dashed border-black/15 p-6 text-sm text-black/60 hover:border-[color:var(--ib-orange)]" data-testid="quote-file-dropzone">
                  <Upload size={22} /> Drag files here or click to upload
                  <span className="text-xs">{(form.allowed_ext || []).join(", ").toUpperCase()} · up to {form.max_file_mb} MB each</span>
                </button>
                <input ref={fileRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="quote-file-input" />
                {files.map((x, i) => <p key={i} className="mt-2 flex items-center justify-between bg-[color:var(--ib-off)] px-3 py-2 text-sm">{x.name}<button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove file"><X size={14} /></button></p>)}
              </div>
              <label className="flex gap-3 text-sm text-black/70 sm:col-span-2">
                <input type="checkbox" name="privacy_ack" checked={f.privacy_ack} onChange={on} className="mt-1 h-4 w-4 accent-[color:var(--ib-orange)]" data-testid="quote-privacy-checkbox" />
                <span>{form.privacy_text}</span>
              </label>
              {err && <p className="bg-red-50 p-3 text-sm text-red-700 sm:col-span-2" role="alert" data-testid="quote-error">{err}</p>}
              <button disabled={busy} className="ib-btn ib-btn-primary justify-center disabled:opacity-60 sm:col-span-2" data-testid="quote-submit-btn">{busy ? "Sending…" : "Request my free estimate"}</button>
            </form>
          )}
          <aside className="ib-grain h-fit bg-[color:var(--ib-charcoal)] p-8 text-white" data-testid="contact-info">
            <p className="font-display text-2xl uppercase">Contact Ironbark</p>
            <ul className="mt-6 space-y-4 text-white/85">
              <li className="flex gap-3"><Phone size={18} className="text-[color:var(--ib-orange)]" /><a href={`tel:${(c.phone || "").replace(/\D/g, "")}`}>{c.phone}</a></li>
              <li className="flex gap-3"><Mail size={18} className="text-[color:var(--ib-orange)]" /><span className="flex flex-col"><a href={`mailto:${c.email}`}>{c.email}</a><a href={`mailto:${c.email2}`}>{c.email2}</a></span></li>
              <li className="flex gap-3"><MapPin size={18} className="text-[color:var(--ib-orange)]" />{c.address}</li>
              <li className="flex gap-3"><Clock size={18} className="text-[color:var(--ib-orange)]" />{c.hours}</li>
            </ul>
            <p className="mt-6 border-t border-white/10 pt-6 text-xs uppercase tracking-widest text-white/50">{c.license_label} · Lic. #{c.license}</p>
          </aside>
        </div>
      </section>
      <GoogleMapCard />
    </main>
  );
}
